import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  LocationCreateInput,
  LocationDetails,
  LocationListFilters,
  LocationListItem,
  LocationSearchResult,
  LocationUpdateInput,
} from '../types/location';
import { CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import { getMasterDataStatusLabel } from '../utils/masterDataStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

const DETAILS_PAGE_SIZE = 100;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function requiredBool(value: unknown, label: string): boolean {
  const parsed = boolOrNull(value);
  if (parsed === null) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeListItem(row: ObjectValue): LocationListItem | null {
  try {
    const isActive = boolOrNull(row.isActive);
    return {
      locationId: requiredId(row.locationId, 'locationId'),
      locationCode: textOrNull(row.locationCode) ?? String(row.locationId),
      locationName: textOrNull(row.locationName) ?? 'Location',
      locationType: textOrNull(row.locationType),
      city: textOrNull(row.city),
      stateName: textOrNull(row.stateName),
      countryCode: textOrNull(row.countryCode),
      timeZone: textOrNull(row.timeZone),
      isHeadOffice: boolOrNull(row.isHeadOffice),
      isAttendanceEnabled: boolOrNull(row.isAttendanceEnabled),
      isActive,
      statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
      rowVersion: numberOrNull(row.rowVersion),
    };
  } catch {
    return null;
  }
}

function normalizeDetails(row: ObjectValue): LocationDetails {
  const isActive = boolOrNull(row.isActive);
  return {
    locationId: requiredId(row.locationId, 'locationId'),
    locationCode: requiredText(row.locationCode, 'locationCode'),
    locationName: requiredText(row.locationName, 'locationName'),
    locationType: requiredText(row.locationType, 'locationType'),
    addressLine1: textOrNull(row.addressLine1),
    addressLine2: textOrNull(row.addressLine2),
    city: requiredText(row.city, 'city'),
    stateName: requiredText(row.stateName, 'stateName'),
    countryCode: requiredText(row.countryCode, 'countryCode'),
    postalCode: textOrNull(row.postalCode),
    latitude: numberOrNull(row.latitude),
    longitude: numberOrNull(row.longitude),
    geofenceRadiusMeters: numberOrNull(row.geofenceRadiusMeters),
    timeZone: requiredText(row.timeZone, 'timeZone'),
    contactPerson: textOrNull(row.contactPerson),
    contactNumber: textOrNull(row.contactNumber),
    isHeadOffice: boolOrNull(row.isHeadOffice) ?? false,
    isAttendanceEnabled: boolOrNull(row.isAttendanceEnabled) ?? false,
    isActive,
    statusLabel: getMasterDataStatusLabel(isActive, textOrNull(row.statusCode)),
    rowVersion: requiredId(row.rowVersion, 'rowVersion'),
  };
}

function buildListParams(
  page: number,
  pageSize: number,
  filters: LocationListFilters = {},
) {
  const params: Record<string, string | number | boolean> = {
    page,
    pageSize,
  };
  const search = filters.search?.trim();
  if (search) params.search = search;
  if (filters.isActive === true || filters.isActive === false) {
    params.isActive = filters.isActive;
  }
  return params;
}

export async function getLocations(options: {
  page?: number;
  pageSize?: number;
  filters?: LocationListFilters;
} = {}): Promise<LocationSearchResult> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const pageSize =
    options.pageSize && options.pageSize > 0 ? options.pageSize : 20;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: number;
        pageSize?: number;
        totalRecords?: number;
        totalPages?: number;
      };
    }>
  >(API_ENDPOINTS.LOCATIONS.LIST, {
    params: buildListParams(page, pageSize, options.filters),
  });

  const data = response.data.data ?? {};
  const items = Array.isArray(data.items)
    ? data.items.map(normalizeListItem).filter((item): item is LocationListItem => item != null)
    : [];
  const pagination = data.pagination ?? {};
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/**
 * No dedicated GET-by-id route. Locate the record through tenant-scoped search
 * pages (same approach the backend uses for ownership checks).
 */
export async function getLocationDetails(
  locationId: number,
): Promise<LocationDetails> {
  if (!Number.isInteger(locationId) || locationId <= 0) {
    throw Object.assign(new Error('locationId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    const response = await apiClient.get<
      Envelope<{
        items?: ObjectValue[];
        pagination?: { totalPages?: number; totalRecords?: number };
      }>
    >(API_ENDPOINTS.LOCATIONS.LIST, {
      params: { page, pageSize: DETAILS_PAGE_SIZE },
    });
    const data = response.data.data ?? {};
    const items = Array.isArray(data.items) ? data.items : [];
    const match = items.find(row => numberOrNull(row.locationId) === locationId);
    if (match) return normalizeDetails(match);

    const totalRecords = numberOrNull(data.pagination?.totalRecords) ?? 0;
    totalPages =
      numberOrNull(data.pagination?.totalPages) ??
      Math.max(1, Math.ceil(totalRecords / DETAILS_PAGE_SIZE));
    page += 1;
  }

  throw Object.assign(new Error('Location was not found.'), {
    code: 'LOCATION_NOT_FOUND',
  });
}

export async function createLocation(
  input: LocationCreateInput,
): Promise<LocationDetails> {
  const body = {
    locationCode: input.locationCode.trim(),
    locationName: input.locationName.trim(),
    locationType: input.locationType.trim(),
    addressLine1: emptyToNull(input.addressLine1),
    addressLine2: emptyToNull(input.addressLine2),
    city: input.city.trim(),
    stateName: input.stateName.trim(),
    countryCode: input.countryCode.trim().toUpperCase(),
    postalCode: emptyToNull(input.postalCode),
    latitude: input.latitude ?? null,
    longitude: input.longitude ?? null,
    geofenceRadiusMeters: input.geofenceRadiusMeters ?? null,
    timeZone: input.timeZone.trim(),
    contactPerson: emptyToNull(input.contactPerson),
    contactNumber: emptyToNull(input.contactNumber),
    isHeadOffice: input.isHeadOffice,
    isAttendanceEnabled: input.isAttendanceEnabled,
  };

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.LOCATIONS.CREATE,
    body,
  );
  return normalizeDetails({
    ...body,
    isActive: true,
    rowVersion: 1,
    ...(response.data.data ?? {}),
  });
}

export async function updateLocation(
  locationId: number,
  input: LocationUpdateInput,
): Promise<LocationDetails> {
  if (!Number.isInteger(locationId) || locationId <= 0) {
    throw Object.assign(new Error('locationId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const body = {
    locationName: input.locationName.trim(),
    locationType: input.locationType.trim(),
    addressLine1: emptyToNull(input.addressLine1),
    addressLine2: emptyToNull(input.addressLine2),
    city: input.city.trim(),
    stateName: input.stateName.trim(),
    countryCode: input.countryCode.trim().toUpperCase(),
    postalCode: emptyToNull(input.postalCode),
    latitude: input.latitude ?? null,
    longitude: input.longitude ?? null,
    geofenceRadiusMeters: input.geofenceRadiusMeters ?? null,
    timeZone: input.timeZone.trim(),
    contactPerson: emptyToNull(input.contactPerson),
    contactNumber: emptyToNull(input.contactNumber),
    isHeadOffice: input.isHeadOffice,
    isAttendanceEnabled: input.isAttendanceEnabled,
    isActive: requiredBool(input.isActive, 'isActive'),
    rowVersion: input.rowVersion,
  };

  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.LOCATIONS.UPDATE(locationId),
    body,
  );
  return normalizeDetails({
    locationId,
    locationCode: '—',
    ...body,
    ...(response.data.data ?? {}),
  });
}

export async function deactivateLocation(
  item: LocationDetails,
): Promise<LocationDetails> {
  return updateLocation(item.locationId, {
    locationName: item.locationName,
    locationType: item.locationType,
    addressLine1: item.addressLine1,
    addressLine2: item.addressLine2,
    city: item.city,
    stateName: item.stateName,
    countryCode: item.countryCode,
    postalCode: item.postalCode,
    latitude: item.latitude,
    longitude: item.longitude,
    geofenceRadiusMeters: item.geofenceRadiusMeters,
    timeZone: item.timeZone,
    contactPerson: item.contactPerson,
    contactNumber: item.contactNumber,
    isHeadOffice: item.isHeadOffice,
    isAttendanceEnabled: item.isAttendanceEnabled,
    isActive: CONFIGURATION_RECORD_INACTIVE,
    rowVersion: item.rowVersion,
  });
}
